import React, { useState, useEffect, useCallback } from 'react';
import { Bookmark, X, Save, Loader } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import {
  getLagerSavedFilters,
  createLagerSavedFilter,
  deleteLagerSavedFilter,
} from 'core/api/apiService';

/**
 * Gespeicherte-Filter-Leiste fuer das Lager-Modul (Phase 3b).
 * Wird direkt ueber der Tabelle in LagerView gerendert.
 *
 * Props:
 *   user            Object    (aus useAuth)
 *   currentFilters  Object    — aktueller Filter-State (wird beim Speichern uebergeben)
 *   onApplyFilter   (filter: Object) => void  — wendet alle gespeicherten Filterwerte an
 *   showSuccess     (msg) => void
 *   showError       (msg) => void
 */
const SavedFilters = ({ user, currentFilters, onApplyFilter, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [savedFilters, setSavedFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveNameError, setSaveNameError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const loadFilters = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLagerSavedFilters(user);
      setSavedFilters(Array.isArray(data) ? data : []);
    } catch {
      // nicht-kritisch; leere Liste anzeigen
      setSavedFilters([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const handleOpenSaveModal = () => {
    setSaveName('');
    setSaveNameError('');
    setSaveModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmedName = saveName.trim();
    if (!trimmedName) {
      setSaveNameError(t('lm.savedFilterNameRequired'));
      return;
    }
    setSaving(true);
    setSaveNameError('');
    try {
      // Nur sinnvolle Filter-Keys uebergeben (undefined/leere Arrays/Defaults weglassen)
      const filterPayload = {};
      if (currentFilters.werk) filterPayload.werk = currentFilters.werk;
      if (currentFilters.search) filterPayload.search = currentFilters.search;
      if (Array.isArray(currentFilters.warengruppe) && currentFilters.warengruppe.length > 0)
        filterPayload.warengruppe = currentFilters.warengruppe.join(',');
      if (Array.isArray(currentFilters.materialart) && currentFilters.materialart.length > 0)
        filterPayload.materialart = currentFilters.materialart.join(',');
      if (currentFilters.bestandsstatus && currentFilters.bestandsstatus !== 'alle')
        filterPayload.bestandsstatus = currentFilters.bestandsstatus;
      if (Array.isArray(currentFilters.lagerort) && currentFilters.lagerort.length > 0)
        filterPayload.lagerort = currentFilters.lagerort.join(',');
      if (Array.isArray(currentFilters.beschaffungsart) && currentFilters.beschaffungsart.length > 0)
        filterPayload.beschaffungsart = currentFilters.beschaffungsart.join(',');
      if (Array.isArray(currentFilters.dispomerkmal) && currentFilters.dispomerkmal.length > 0)
        filterPayload.dispomerkmal = currentFilters.dispomerkmal.join(',');
      if (currentFilters.bestand_min != null && currentFilters.bestand_min !== '')
        filterPayload.bestand_min = String(currentFilters.bestand_min);
      if (currentFilters.bestand_max != null && currentFilters.bestand_max !== '')
        filterPayload.bestand_max = String(currentFilters.bestand_max);
      if (currentFilters.offene_banf && currentFilters.offene_banf !== 'alle')
        filterPayload.offene_banf = currentFilters.offene_banf;
      if (currentFilters.offene_bestellung && currentFilters.offene_bestellung !== 'alle')
        filterPayload.offene_bestellung = currentFilters.offene_bestellung;
      if (currentFilters.handlungsbedarf === true)
        filterPayload.handlungsbedarf = 'true';
      if (Array.isArray(currentFilters.stock_type) && currentFilters.stock_type.length > 0)
        filterPayload.stock_type = currentFilters.stock_type.join(',');
      if (Array.isArray(currentFilters.vendor) && currentFilters.vendor.length > 0)
        filterPayload.vendor = currentFilters.vendor.join(',');
      if (Array.isArray(currentFilters.ekgrp) && currentFilters.ekgrp.length > 0)
        filterPayload.ekgrp = currentFilters.ekgrp.join(',');
      if (currentFilters.sperrbestand === true)
        filterPayload.sperrbestand = 'true';
      if (currentFilters.qmbestand === true)
        filterPayload.qmbestand = 'true';
      if (Array.isArray(currentFilters.merkmale) && currentFilters.merkmale.length > 0)
        filterPayload.merkmale = currentFilters.merkmale;

      await createLagerSavedFilter(user, { name: trimmedName, filter: filterPayload });
      showSuccess(t('lm.savedFilterSaved'));
      setSaveModalOpen(false);
      await loadFilters();
    } catch (err) {
      const msg = err?.message || t('lm.savedFilterSaveError');
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('lm.deleteConfirm'))) return;
    setDeletingId(id);
    try {
      await deleteLagerSavedFilter(user, id);
      showSuccess(t('lm.savedFilterDeleted'));
      await loadFilters();
    } catch {
      showError(t('lm.savedFilterDeleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleApply = (filter) => {
    // filter ist ein Objekt mit flachen Werten aus dem Backend
    // In Array-Felder umwandeln wo noetig
    const parsed = {};
    if (filter.werk) parsed.werk = filter.werk;
    if (filter.search) parsed.search = filter.search;
    if (filter.warengruppe)
      parsed.warengruppe = typeof filter.warengruppe === 'string'
        ? filter.warengruppe.split(',').filter(Boolean)
        : Array.isArray(filter.warengruppe) ? filter.warengruppe : [];
    if (filter.materialart)
      parsed.materialart = typeof filter.materialart === 'string'
        ? filter.materialart.split(',').filter(Boolean)
        : Array.isArray(filter.materialart) ? filter.materialart : [];
    if (filter.bestandsstatus) parsed.bestandsstatus = filter.bestandsstatus;
    if (filter.lagerort)
      parsed.lagerort = typeof filter.lagerort === 'string'
        ? filter.lagerort.split(',').filter(Boolean)
        : Array.isArray(filter.lagerort) ? filter.lagerort : [];
    if (filter.beschaffungsart)
      parsed.beschaffungsart = typeof filter.beschaffungsart === 'string'
        ? filter.beschaffungsart.split(',').filter(Boolean)
        : Array.isArray(filter.beschaffungsart) ? filter.beschaffungsart : [];
    if (filter.dispomerkmal)
      parsed.dispomerkmal = typeof filter.dispomerkmal === 'string'
        ? filter.dispomerkmal.split(',').filter(Boolean)
        : Array.isArray(filter.dispomerkmal) ? filter.dispomerkmal : [];
    if (filter.bestand_min != null) parsed.bestand_min = filter.bestand_min;
    if (filter.bestand_max != null) parsed.bestand_max = filter.bestand_max;
    if (filter.offene_banf) parsed.offene_banf = filter.offene_banf;
    if (filter.offene_bestellung) parsed.offene_bestellung = filter.offene_bestellung;
    if (filter.handlungsbedarf === 'true' || filter.handlungsbedarf === true)
      parsed.handlungsbedarf = true;
    if (filter.stock_type)
      parsed.stock_type = typeof filter.stock_type === 'string' ? filter.stock_type.split(',').filter(Boolean) : (Array.isArray(filter.stock_type) ? filter.stock_type : []);
    if (filter.vendor)
      parsed.vendor = typeof filter.vendor === 'string' ? filter.vendor.split(',').filter(Boolean) : (Array.isArray(filter.vendor) ? filter.vendor : []);
    if (filter.ekgrp)
      parsed.ekgrp = typeof filter.ekgrp === 'string' ? filter.ekgrp.split(',').filter(Boolean) : (Array.isArray(filter.ekgrp) ? filter.ekgrp : []);
    if (filter.sperrbestand === 'true' || filter.sperrbestand === true)
      parsed.sperrbestand = true;
    if (filter.qmbestand === 'true' || filter.qmbestand === true)
      parsed.qmbestand = true;
    if (Array.isArray(filter.merkmale)) parsed.merkmale = filter.merkmale;

    onApplyFilter(parsed);
    showSuccess(t('lm.savedFilterApplied'));
  };

  const chipBase =
    'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer select-none';
  const chipInactive = isDarkMode
    ? 'bg-white/8 border-white/15 text-white/60 hover:bg-white/12'
    : 'bg-white border-slate-200 text-slate-600 hover:border-brand/30 hover:text-brand';

  return (
    <>
      <div className={`px-4 py-2.5 border-b flex flex-wrap items-center gap-2 ${
        isDarkMode ? 'bg-gray-900/10 border-white/10' : 'bg-white border-slate-100'
      }`}>
        {/* Titel */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Bookmark className={`w-3.5 h-3.5 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
          <span className={`text-xs font-medium ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
            {t('lm.savedFiltersTitle')}:
          </span>
        </div>

        {/* Loading */}
        {loading && (
          <Loader className={`w-3.5 h-3.5 animate-spin ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
        )}

        {/* Leer-Zustand */}
        {!loading && savedFilters.length === 0 && (
          <span className={`text-xs ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>
            {t('lm.emptySaved')}
          </span>
        )}

        {/* Gespeicherte Filter als Chips */}
        {!loading && Array.isArray(savedFilters) && savedFilters.map((sf) => (
          <span
            key={sf.id}
            className={`${chipBase} ${chipInactive} group`}
          >
            <button
              type="button"
              onClick={() => handleApply(sf.filter || {})}
              className="focus:outline-none"
            >
              {sf.name}
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDelete(sf.id); }}
              disabled={deletingId === sf.id}
              className={`ml-0.5 transition-colors ${
                deletingId === sf.id ? 'opacity-50' : 'hover:text-red-400'
              }`}
              aria-label={t('common.delete')}
            >
              {deletingId === sf.id
                ? <Loader className="w-3 h-3 animate-spin" />
                : <X className="w-3 h-3" />}
            </button>
          </span>
        ))}

        {/* Speichern-Button */}
        <button
          type="button"
          onClick={handleOpenSaveModal}
          className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all ${
            isDarkMode
              ? 'bg-brand/20 border-brand/40 text-blue-200 hover:bg-brand/30'
              : 'bg-brand/8 border-brand/30 text-brand hover:bg-brand/15'
          }`}
        >
          <Save className="w-3 h-3" />
          {t('lm.saveCurrent')}
        </button>
      </div>

      {/* Speichern-Modal — inline, kein position:fixed */}
      {saveModalOpen && (
        <div className={`px-4 py-3 border-b ${isDarkMode ? 'bg-gray-900/30 border-white/10' : 'bg-brand/4 border-brand/20'}`}>
          <form onSubmit={handleSave} className="flex flex-wrap items-center gap-2">
            <span className={`text-xs font-semibold ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
              {t('lm.saveModalTitle')}:
            </span>
            <input
              type="text"
              value={saveName}
              onChange={(e) => { setSaveName(e.target.value); if (saveNameError) setSaveNameError(''); }}
              placeholder={t('lm.saveModalNamePlaceholder')}
              maxLength={120}
              autoFocus
              className={`flex-1 min-w-[180px] max-w-xs text-xs rounded-lg border px-2.5 py-1.5 outline-none transition-all ${
                isDarkMode
                  ? 'bg-white/8 border-white/15 text-white placeholder-white/30 focus:border-brand/50'
                  : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400 focus:border-brand/50 focus:ring-1 focus:ring-brand/20'
              } ${saveNameError ? 'border-red-400' : ''}`}
            />
            {saveNameError && (
              <span className="text-red-400 text-xs">{saveNameError}</span>
            )}
            <button
              type="submit"
              disabled={saving}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-50 ${
                isDarkMode
                  ? 'bg-brand text-white hover:bg-brand/90'
                  : 'bg-brand text-white hover:bg-brand/90'
              }`}
            >
              {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {saving ? t('common.saving') : t('lm.save')}
            </button>
            <button
              type="button"
              onClick={() => setSaveModalOpen(false)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                isDarkMode
                  ? 'text-white/60 hover:text-white/80 hover:bg-white/8'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              {t('lm.cancel')}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default SavedFilters;
